import React, { useState, useContext } from "react";
import tw from "twin.macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import Header from "./../components/headers/HeaderLight";
import { SectionHeading as HeadingBase } from "components/misc/Headings";
import GymllustrationSrc from "images/demo/gym.png";
import TerracellustrationSrc from "images/demo/terrace.png";
import GrillllustrationSrc from "images/demo/grill.png";
import SwimmingPoolllustrationSrc from "images/demo/swimming-pool.png";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { Carousel, CarouselItem, CarouselIndicators, CarouselCaption, Row, Button } from "reactstrap";
import { ROUTE_PATHS } from "utils/routes.js";
import { SectionDescription as DescriptionBase } from "components/misc/Typography";
import { Container } from "components/misc/Layouts";
import AmenitiesTable from "components/tables/AmenitiesTable.js";
import AmenitiesAccordion from "components/accordions/AmenitiesAccordion.js";
import { useAmenities } from "hooks/useAmenities.js";
import { AppContext } from "components/context/AppContext.js";
import LoadingSpinner from "components/misc/LoadingSpinner.js";

const SpinnerOverlay = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background: rgba(255,255,255,0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Column = tw.div`flex-1`;
const ImageColumn = tw(Column)`mx-auto lg:mr-0 relative mt-16 lg:mt-0 lg:ml-8`;
const ImageContainer = tw.div`items-end`;
const Image = tw.img`max-w-full rounded-t sm:rounded`;
const TextColumn = tw.div`text-center lg:text-left`;
const Heading = tw(HeadingBase)`text-center lg:text-left text-primary-900 leading-snug`;
const Description = tw(
    DescriptionBase
)`mt-4 text-center lg:text-left lg:text-base text-gray-700 max-w-lg mx-auto lg:mx-0`;

const HeroRow = tw(Row)`flex-col lg:flex-row justify-between items-end pt-8 lg:pt-12 pb-16 max-w-screen-2xl mx-auto flex-wrap`;
const ButtonTw = tw(
    Button
)`px-8 py-3 font-bold rounded bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300 mt-12 inline-block tracking-wide text-center px-10 py-4 font-semibold tracking-normal`;

const items = [
    {
        heading: "Alberca",
        description: "Reserva la alberca para uso exclusivo (1 hora máximo, una vez por semana) o en modo compartido sin límite de tiempo. Confirma con al menos 24 horas de anticipación.",
        rules: [
            "Es obligatorio el uso de traje de baño.",
            "No se permite el ingreso de alimentos ni bebidas alcohólicas.",
            "Los menores deben estar acompañados por un adulto.",
            "La reservación debe realizarse con al menos 24 horas de anticipación.",
            "No se permite el ingreso de mascotas.",
            "No se permite música a alto volumen.",
            "La reservación es por un tiempo de 2 horas."
        ],
        src: SwimmingPoolllustrationSrc,
        altText: 'Slide 1',
        caption: '',
        key: 1,
    },
    {
        heading: "Terraza",
        description: "Reserva la terraza para eventos privados, disponible una vez cada 6 meses. Horario de uso: 10:00 a 22:00 hrs. Capacidad máxima según reglamento.",
        rules: [
            "Horario de uso: 10:00 a 22:00 hrs.",
            "No se permite el uso de pirotecnia ni fuegos artificiales.",
            "Mantén la limpieza y orden al finalizar el evento.",
            "El responsable debe estar presente durante todo el evento.",
            "Respeta el aforo máximo permitido."
        ],
        src: TerracellustrationSrc,
        altText: 'Slide 2',
        caption: '',
        key: 2,
    },
    {
        heading: "Asadores",
        description: "Reserva los asadores disponibles para tus reuniones privadas, máximo una vez cada 3 meses. Deja el área limpia al finalizar y respeta los horarios asignados.",
        rules: [
            "No se permite el uso de carbón fuera de los asadores designados.",
            "Limpia el área después de su uso.",
            "No dejes objetos personales ni basura.",
            "Respeta el horario de uso y el tiempo de reservación.",
            "No se permite música a alto volumen."
        ],
        src: GrillllustrationSrc,
        altText: 'Slide 3',
        caption: '',
        key: 3,
    },
    {
        heading: "Gimnasio",
        description: "Reserva el uso del gimnasio en los horarios disponibles respetando el cupo máximo.",
        rules: [
            "Usa toalla personal y limpia los aparatos después de usarlos.",
            "No se permite el ingreso de alimentos ni bebidas alcohólicas.",
            "El uso de calzado deportivo es obligatorio.",
            "Respeta el tiempo de reservación y el cupo máximo (10 personas).",
            "No se permite el acceso a menores de edad sin supervisión.",
            "1.5 horas de uso máximo de 6 AM a 10:30 PM."
        ],
        src: GymllustrationSrc,
        altText: 'Slide 4',
        caption: '',
        key: 4,
    },
];

const AmenitiesPage = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [animating, setAnimating] = useState(false);
    const { state } = useContext(AppContext);
    const homeownerId = state.user?.homeowner?.homeownerId;
    const { amenitiesByHome, loading, fetchAmenitiesByHome, fetchAmenities, getReservedAmenitiesByType } = useAmenities();

    const next = () => {
        if (animating) return;
        const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
        setActiveIndex(nextIndex);
    };

    const previous = () => {
        if (animating) return;
        const nextIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
        setActiveIndex(nextIndex);
    };

    const goToIndex = (newIndex) => {
        if (animating) return;
        setActiveIndex(newIndex);
    };
  
    React.useEffect(() => {
        const fetchData = async () => {
            await fetchAmenitiesByHome(homeownerId);
            await fetchAmenities();
            await getReservedAmenitiesByType('Alberca');
        };
        fetchData();
    }, []);

    const slides = items.map((item) => {
        return (
            <CarouselItem
                onExiting={() => setAnimating(true)}
                onExited={() => setAnimating(false)}
                key={item.src}
            >
                <Container tw="bg-blue-100 -mx-8 -mt-8 pt-8 px-8 mt-12" className="max-w-6xl mx-auto">
                    <HeroRow>
                        <TextColumn>
                            <Heading as="h1">{item.heading}</Heading>
                            {/* Description: left on mobile, right on lg+ */}
                            <div className="lg:absolute lg:right-0 lg:top-1/4 lg:w-1/2 lg:text-right">
                                <Description className="lg:text-right lg:mx-0 lg:max-w-xl lg:mt-12">
                                    {item.description}
                                    <br />
                                    <span className="font-bold block mt-4 mb-2">Reglas y Términos:</span>
                                    <ul className="list-disc pl-5 text-left">
                                        {item.rules?.map((line) => (
                                            <li key={item.heading + '-' + line} className="mb-1">{line}</li>
                                        ))}
                                    </ul>
                                </Description>
                            </div>
                        </TextColumn>
                        <ImageColumn>
                            <ImageContainer>
                                <Image src={item.src} className="max-h-[460px] w-auto" />
                            </ImageContainer>
                        </ImageColumn>
                    </HeroRow>
                </Container>
                <div>
                    <CarouselCaption
                        captionText={item.caption}
                        captionHeader={item.caption}
                    />
                </div>
            </CarouselItem>
        );
    });

    if (loading) {
        return (
            <SpinnerOverlay>
                <LoadingSpinner />
            </SpinnerOverlay>
        );
    }

    return (
        <AnimationRevealPage>
            <Header />
            <Carousel
                activeIndex={activeIndex}
                next={next}
                previous={previous}
            >
                <CarouselIndicators
                    items={items}
                    className="carousel-indicators"
                    activeIndex={activeIndex}
                    onClickHandler={goToIndex}
                />
                {slides}
            </Carousel>
            <div className="flex flex-col items-start px-24 mb-12 mt-12 gap-2">
                <ButtonTw className="my-2" size="lg" href={ROUTE_PATHS.AMENITYSELECTION}>
                    Reservar una Amenidad
                </ButtonTw>
                <p className="text-gray-500 text-sm mt-1">Selecciona el espacio, elige fecha y horario disponible. Tu solicitud queda registrada y la administración la confirma.</p>
            </div>

            {/* Desktop Table - Only show PaymentTable on md and up */}
            <div className="hidden md:block w-full px-24">
                <AmenitiesTable title={"Amenidades"} tableData={amenitiesByHome} />
            </div>

            {/* Mobile Accordions - hidden on md and up */}
            <div className="md:hidden px-2 py-4">
                <AmenitiesAccordion tableData={amenitiesByHome} />
            </div>

            <Footer />
        </AnimationRevealPage>
    );
};

export default AmenitiesPage;
